// Plan 24b Phase B, Task 1.5 — Mid-game Scoreboard panel (Screen 17)
//
// Centered panel showing player standings sorted by trophies descending
// (keys as tiebreaker). Displays rank-ordered rows using createScoreboardRow
// with stagger entrance animations.
//
// Toggle behaviour:
//   - If payload.toggleWiring is present, subscribe to 'toggleScoreboard'
//     events to close back to board-hud.
//   - Otherwise fall back to Tab keydown listener.
//   - ESC always closes (named function reference for clean deregistration).

import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';
import type { ToggleWiring } from '@client/input/toggle-wiring';
import { createScoreboardRow } from '@client/ui/components/scoreboard-row';
import type { ScoreboardRowHandle } from '@client/ui/components/scoreboard-row';
import type { GsapTimelineHandle } from '@client/renderer/vfx/gsap-registry';
import { SHAPE_GLYPH_FOR_COLOR, colorIdFromIndex } from './shape-palette';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type ScoreboardPayload = BoardMountPayload & {
  toggleWiring?: ToggleWiring;
};

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — exact values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** typography.family.headline */
const HEADLINE_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";

/** typography.size.h1 = 48px */
const H1_SIZE = '48px';

/** typography.shadow.title */
const TITLE_SHADOW = '0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)';

/** typography.size.caption = 14px */
const CAPTION_SIZE = '14px';

/** color.text.primary = #FFFFFF */
const COLOR_TEXT_PRIMARY = '#FFFFFF';

/** color.text.muted = #707888 */
const COLOR_TEXT_MUTED = '#707888';

/** color.ui.panel-bg = #1E2332 */
const PANEL_BG = '#1E2332';

/** border-radius.lg = 12px */
const RADIUS_LG = '12px';

/** shadow.panel = 0 4px 12px rgba(0,0,0,0.50) */
const SHADOW_PANEL = '0 4px 12px rgba(0,0,0,0.50)';

/** Max HP spec constant */
const MAX_HP = 30;

/** animation.duration.stagger-delay = 0.04s per row */
const STAGGER_DELAY_S = 0.04;

/** layout.panel-padding-lg = '24px 28px' */
const PANEL_PADDING_LG = '24px 28px';

// ─────────────────────────────────────────────────────────────────────────────
// Player color palette (index = Player.color % 10)
// ─────────────────────────────────────────────────────────────────────────────

const PLAYER_COLORS = [
  '#E04040', // 0 red
  '#4080D0', // 1 blue
  '#40B040', // 2 green
  '#E8D020', // 3 yellow
  '#E08820', // 4 orange
  '#9040C0', // 5 purple
  '#E06090', // 6 pink
  '#40B0C0', // 7 cyan
  '#E8E8E8', // 8 white
  '#404040', // 9 black
];

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createScoreboardScreen(ctx: ScreenMountContext<ScoreboardPayload>): ScreenHandle {
  const { host, gsapRegistry, router, payload } = ctx;
  const { state, localPlayerId, toggleWiring } = payload;

  let disposed = false;

  // Track row handles + stagger timeline handles for dispose()
  const rowHandles: ScoreboardRowHandle[] = [];
  const staggerHandles: GsapTimelineHandle[] = [];

  // ── Sort players by trophies desc, keys as tiebreaker ─────────────────────

  const sorted = [...state.turnOrder]
    .map((id) => ({ id, player: state.players.get(id)! }))
    .filter(({ player }) => player != null)
    .sort((a, b) => {
      const trophyDiff = b.player.trophies - a.player.trophies;
      if (trophyDiff !== 0) return trophyDiff;
      return b.player.keys - a.player.keys;
    });

  // ── Root overlay (fixed full-screen backdrop) ──────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-scoreboard';
  root.setAttribute('data-screen-id', 'scoreboard');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    'z-index: 160',
    'pointer-events: auto',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'background: rgba(0, 0, 0, 0.55)',
  ].join('; ');

  // ── Centered panel ─────────────────────────────────────────────────────────

  const panel = document.createElement('div');
  panel.className = 'scoreboard-panel';
  panel.style.cssText = [
    'width: 640px',
    'max-width: calc(100vw - 32px)',
    `background: ${PANEL_BG}`,
    `border-radius: ${RADIUS_LG}`,
    `box-shadow: ${SHADOW_PANEL}`,
    `padding: ${PANEL_PADDING_LG}`,
    'display: flex',
    'flex-direction: column',
    'gap: 12px',
  ].join('; ');

  // ── Title ──────────────────────────────────────────────────────────────────

  const title = document.createElement('h1');
  title.className = 'scoreboard-title';
  title.textContent = 'STANDINGS';
  title.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    `font-size: ${H1_SIZE}`,
    `color: ${COLOR_TEXT_PRIMARY}`,
    `text-shadow: ${TITLE_SHADOW}`,
    'text-transform: uppercase',
    'margin: 0 0 8px',
    'text-align: center',
    'font-weight: 700',
    'line-height: 1',
  ].join('; ');

  panel.appendChild(title);

  // ── Rows container ─────────────────────────────────────────────────────────

  const rowsContainer = document.createElement('div');
  rowsContainer.className = 'scoreboard-rows';
  rowsContainer.style.cssText = ['display: flex', 'flex-direction: column', 'gap: 4px'].join('; ');

  // Render one row per player in sorted order with stagger entrance
  sorted.forEach(({ id, player }, i) => {
    const rank = i + 1;
    const playerColor = PLAYER_COLORS[player.color % 10] ?? PLAYER_COLORS[0];
    const isLocal = id === localPlayerId;

    // Row container element that createScoreboardRow will append into
    const rowMount = document.createElement('div');
    rowMount.className = 'scoreboard-row-mount';
    rowMount.style.cssText = ['display: flex', 'align-items: center', 'gap: 6px'].join('; ');

    // Shape glyph for color-blind accessibility
    const glyphSpan = document.createElement('span');
    glyphSpan.className = 'scoreboard-shape-glyph';
    const colorId = colorIdFromIndex(player.color % 10);
    glyphSpan.textContent = (colorId ? SHAPE_GLYPH_FOR_COLOR[colorId] : null) ?? '●';
    glyphSpan.style.cssText = [
      `font-size: 12px`,
      `color: ${playerColor}`,
      'flex-shrink: 0',
      'user-select: none',
    ].join('; ');
    rowMount.appendChild(glyphSpan);

    // Create the row using the component primitive
    const rowHandle = createScoreboardRow({
      gsapRegistry,
      mount: rowMount,
      playerName: player.name,
      playerColor,
      currentHP: player.hp,
      maxHP: MAX_HP,
      keys: player.keys,
      trophies: player.trophies,
      rank,
    });

    rowHandles.push(rowHandle);

    // Local player gets a subtle highlight
    if (isLocal) {
      rowHandle.element.style.background = 'rgba(255,255,255,0.06)';
    }

    // Stagger entrance animation
    const tlHandle = gsapRegistry.create(`scoreboard:row-${i}`);
    staggerHandles.push(tlHandle);
    tlHandle.timeline.fromTo(
      rowHandle.element,
      { x: -20, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.3 },
      i * STAGGER_DELAY_S,
    );

    rowsContainer.appendChild(rowMount);
  });

  panel.appendChild(rowsContainer);

  // ── Close hint ─────────────────────────────────────────────────────────────

  const closeHint = document.createElement('div');
  closeHint.className = 'scoreboard-close-hint';
  closeHint.textContent = 'Press Tab to close';
  closeHint.style.cssText = [
    `font-size: ${CAPTION_SIZE}`,
    `color: ${COLOR_TEXT_MUTED}`,
    'text-align: center',
    'margin-top: 4px',
  ].join('; ');

  panel.appendChild(closeHint);
  root.appendChild(panel);

  // ── Mount to host ─────────────────────────────────────────────────────────

  host.appendChild(root);

  // ── Toggle / keyboard close handlers ─────────────────────────────────────

  let unsubscribeToggle: (() => void) | null = null;

  if (toggleWiring) {
    // Primary: subscribe to toggleWiring
    unsubscribeToggle = toggleWiring.subscribe((evt) => {
      if (disposed) return;
      if (evt.name === 'toggleScoreboard') {
        router.navigate('board-hud', payload);
      }
    });
  } else {
    // Fallback: listen for Tab
    function handleTabKeyDown(e: KeyboardEvent): void {
      if (disposed) return;
      if (e.key === 'Tab') {
        e.preventDefault();
        router.navigate('board-hud', payload);
      }
    }
    document.addEventListener('keydown', handleTabKeyDown);
    // Store cleanup in unsubscribeToggle slot for dispose()
    unsubscribeToggle = () => {
      document.removeEventListener('keydown', handleTabKeyDown);
    };
  }

  // ESC always closes — named function for clean deregistration
  function handleEsc(e: KeyboardEvent): void {
    if (disposed) return;
    if (e.key === 'Escape') {
      router.navigate('board-hud', payload);
    }
  }

  document.addEventListener('keydown', handleEsc);

  // ── dispose ───────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Unsubscribe toggle wiring (or remove keyboard fallback)
    if (unsubscribeToggle) {
      unsubscribeToggle();
      unsubscribeToggle = null;
    }

    // Remove ESC listener
    document.removeEventListener('keydown', handleEsc);

    // Dispose each row handle
    for (const rowHandle of rowHandles) {
      rowHandle.dispose();
    }

    // Kill each stagger timeline handle
    for (const tlHandle of staggerHandles) {
      tlHandle.dispose();
    }

    // Remove from DOM
    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
